import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, ChevronLeft } from "lucide-react";
import { FileUploader } from "@/components/common/FileUploader";

const TABS = [
  { key: "student", label: "Student" },
  { key: "national", label: "National IDs" },
  { key: "father", label: "Father" },
  { key: "mother", label: "Mother" },
  { key: "guardian", label: "Guardian" },
  { key: "address", label: "Address" },
  { key: "emergency", label: "Emergency" },
  { key: "asp", label: "ASP" },
  { key: "medical", label: "Medical" },
  { key: "docs", label: "Documents" },
];

const FIELDS = {
  student: [
    ["admission_no", "Admission No"],
    ["admission_date", "Admission Date", "date"],
    ["admission_year", "Admission Year", "select", "academic_years"],
    [
      "student_type",
      "Student Type",
      "select",
      null,
      [
        { value: "new", label: "New" },
        { value: "old", label: "Old" },
        { value: "transfer", label: "Transfer" },
      ],
    ],
    ["first_name", "First Name", "text", null, null, true],
    ["middle_name", "Middle Name"],
    ["last_name", "Last Name"],
    ["class_id", "Class", "select", "classes", null, true],
    ["section", "Section", "select", "class_sections"],
    [
      "has_sibling",
      "Has Sibling",
      "select",
      null,
      [
        { value: "no", label: "No" },
        { value: "yes", label: "Yes" },
      ],
    ],
    ["sibling_student_id", "Sibling Student", "select", "siblings"],
    ["roll_no", "Roll No"],
    [
      "gender",
      "Gender",
      "select",
      null,
      [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
        { value: "Other", label: "Other" },
      ],
      true,
    ],
    ["date_of_birth", "Date of Birth", "date", null, null, true],
    ["place_of_birth", "Place of Birth"],
    ["nationality", "Nationality"],
    [
      "category",
      "Category",
      "select",
      null,
      [
        { value: "General", label: "General" },
        { value: "OBC", label: "OBC" },
        { value: "SC", label: "SC" },
        { value: "ST", label: "ST" },
        { value: "EWS", label: "EWS" },
      ],
    ],
    ["caste", "Caste"],
    ["religion", "Religion"],
    ["mobile", "Mobile", "text", null, null, true],
    ["email", "Email"],
    ["password", "Login Password", "password"],
    [
      "blood_group",
      "Blood Group",
      "select",
      null,
      ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map((b) => ({
        value: b,
        label: b,
      })),
    ],
    ["height", "Height (cm)", "number"],
    ["weight", "Weight (kg)", "number"],
    ["house_id", "House"],
    ["club_id", "Club"],
    [
      "rte",
      "RTE Beneficiary",
      "select",
      null,
      [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    ],
  ],
  national: [
    ["aadhaar", "Aadhaar Number"],
    ["apaar_id", "APAAR ID (Auto-generated)"],
    ["pen", "PEN (Personal Education Number)"],
    ["udise_pen", "UDISE+ PEN"],
    ["digilocker_id", "DigiLocker ID"],
    ["sssm_id", "SSSM ID (if applicable)"],
    ["family_id", "Family ID"],
    ["passport_no", "Passport Number"],
    ["passport_valid_till", "Passport Valid Till", "date"],
    [
      "board",
      "Board",
      "select",
      null,
      [
        { value: "CBSE", label: "CBSE" },
        { value: "ICSE", label: "ICSE" },
        { value: "State", label: "State Board" },
        { value: "IB", label: "IB" },
        { value: "IGCSE", label: "IGCSE" },
      ],
    ],
    ["board_roll_no", "Board Roll No"],
    ["sr_no", "School Register No (SR)"],
    ["pen_issue_date", "PEN Issue Date", "date"],
  ],
  father: [
    "first_name",
    "middle_name",
    "last_name",
    "mobile",
    "qualification",
    "occupation",
    "yearly_income",
    "email",
    "pan",
    "aadhaar",
    "photo",
  ].map((k) => [`father_${k}`, "Father " + k.replace("_", " ")]),
  mother: [
    "first_name",
    "middle_name",
    "last_name",
    "mobile",
    "qualification",
    "occupation",
    "yearly_income",
    "email",
    "pan",
    "aadhaar",
    "photo",
  ].map((k) => [`mother_${k}`, "Mother " + k.replace("_", " ")]),
  guardian: [
    [
      "guardian_type",
      "Guardian Type",
      "select",
      null,
      [
        { value: "Father", label: "Father" },
        { value: "Mother", label: "Mother" },
        { value: "Other", label: "Other" },
      ],
    ],
  ].concat(
    [
      "first_name",
      "last_name",
      "mobile",
      "qualification",
      "occupation",
      "email",
      "pan",
      "aadhaar",
      "relation",
      "address",
    ].map((k) => [`guardian_${k}`, "Guardian " + k.replace("_", " ")]),
  ),
  address: [
    ["addr_line1", "Current Address Line 1"],
    ["addr_line2", "Current Address Line 2"],
    ["city", "City"],
    ["state", "State"],
    ["district", "District"],
    ["country", "Country"],
    ["pin", "Pin Code"],
    ["same_as_current", "Same as Current", "checkbox"],
    ["perm_addr_line1", "Permanent Line 1"],
    ["perm_addr_line2", "Permanent Line 2"],
    ["perm_city", "Perm. City"],
    ["perm_state", "Perm. State"],
    ["perm_district", "Perm. District"],
    ["perm_country", "Perm. Country"],
    ["perm_pin", "Perm. Pin"],
  ],
  emergency: [
    [
      "emergency_contact_type",
      "Type",
      "select",
      null,
      [
        { value: "Father", label: "Father" },
        { value: "Mother", label: "Mother" },
        { value: "Guardian", label: "Guardian" },
      ],
    ],
    ["emergency_name", "Contact Person"],
    ["emergency_mobile", "Mobile"],
    ["emergency_phone", "Telephone"],
    ["emergency_address", "Address", "textarea"],
  ],
  asp: [
    "ac_transport",
    "ac_hostel",
    "extracurricular",
    "career_counseling",
    "stationery",
    "books",
    "mess",
    "rte_service",
    "competitive_exam",
    "shadow_teacher",
  ].map((k) => [k, k.replace("_", " "), "checkbox"]),
  medical: [
    ["allergies", "Allergies", "textarea"],
    ["chronic_disease", "Chronic Disease"],
    ["disability_type", "Disability Type"],
    ["special_needs", "Special Needs"],
    ["doctor_name", "Doctor Name"],
    ["doctor_contact", "Doctor Contact"],
    [
      "asthma",
      "Asthma",
      "select",
      null,
      [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ],
    ],
    [
      "heart_disease",
      "Heart Disease",
      "select",
      null,
      [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ],
    ],
  ],
  docs: [
    ["doc_birth_certificate", "Birth Certificate"],
    ["doc_aadhaar_card", "Aadhaar Card"],
    ["doc_prev_marksheet", "Previous Marksheet"],
    ["doc_transfer_certificate", "Transfer Certificate (TC)"],
    ["doc_migration_certificate", "Migration Certificate"],
    ["doc_hostel_affidavit", "Hostel Affidavit"],
    ["doc_grade10_marksheet", "Grade 10 Marksheet"],
    ["doc_bank_passbook", "Bank Passbook Copy"],
    ["doc_address_proof", "Address Proof"],
    ["doc_vaccination_certificate", "Vaccination Certificate"],
    ["doc_student_photo", "Student Photo"],
    ["doc_father_photo", "Father Photo"],
    ["doc_mother_photo", "Mother Photo"],
  ],
};

export default function StudentAdmission() {
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const { branchId, sessionId, isSuperAdmin } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("student");
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, b, s, st] = await Promise.all([
        api.get("/classes"),
        api.get("/branches"),
        api.get("/sessions"),
        api.get("/students", {
          params: { branch_id: branchId, limit: 1000 },
        }),
      ]);
      setClasses(c.data.items || []);
      setBranches(b.data.items || []);
      setSessions(s.data.items || []);
      setSiblings(st.data.items || []);
      if (isEdit) {
        const { data } = await api.get(`/students/${id}`);
        if (data && !data.has_sibling) {
          data.has_sibling = data.sibling_student_id ? "yes" : "no";
        }
        setForm(data);
      } else {
        // Auto-attach branch + session from logged-in context
        setForm({
          admission_date: new Date().toISOString().slice(0, 10),
          branch_id: branchId,
          session_id: sessionId,
          has_sibling: "no",
          status: "active",
        });
      }
    })();
  }, [id, isEdit, branchId, sessionId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When Guardian Type is Father/Mother, pull their details from the
  // Father/Mother tabs so the same info isn't retyped in the Guardian tab.
  const setGuardianType = (value) => {
    setForm((f) => {
      const next = { ...f, guardian_type: value };
      const prefix =
        value === "Father" ? "father" : value === "Mother" ? "mother" : null;
      if (prefix) {
        [
          "first_name",
          "last_name",
          "mobile",
          "qualification",
          "occupation",
          "email",
          "pan",
          "aadhaar",
        ].forEach((k) => {
          next[`guardian_${k}`] = f[`${prefix}_${k}`] || "";
        });
        next.guardian_relation = value;
      }
      return next;
    });
  };

  const renderField = ([key, label, type, src, opts, required]) => {
    let options = opts;
    if (src === "classes")
      options = classes.map((c) => ({ value: c.id, label: c.name }));
    if (src === "academic_years")
      options = sessions.map((s) => ({ value: s.name, label: s.name }));
    if (src === "siblings") {
      options = siblings
        .filter((s) => String(s.id) !== String(form.id || id || ""))
        .map((s) => ({
          value: s.id,
          label:
            `${s.admission_no || ""} - ${s.first_name || ""} ${s.last_name || ""}`.trim(),
        }));
    }
    if (src === "class_sections") {
      const cls = classes.find((c) => c.id === form.class_id);
      options = (cls?.sections || []).map((s) => ({ value: s, label: s }));
    }

    if (key === "sibling_student_id" && (form.has_sibling || "no") !== "yes") {
      return null;
    }

    return (
      <div key={key}>
        <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {type === "select" ? (
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-xs px-2 bg-white"
            value={form[key] || ""}
            onChange={(e) =>
              key === "guardian_type"
                ? setGuardianType(e.target.value)
                : set(key, e.target.value)
            }
            data-testid={`f-${key}`}
          >
            <option value="">—</option>
            {(options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea
            className="w-full rounded-md border border-slate-300 text-xs px-2 py-1.5"
            rows={2}
            value={form[key] || ""}
            onChange={(e) => set(key, e.target.value)}
            data-testid={`f-${key}`}
          />
        ) : type === "checkbox" ? (
          <div className="h-9 flex items-center">
            <input
              type="checkbox"
              checked={!!form[key]}
              onChange={(e) => set(key, e.target.checked)}
              data-testid={`f-${key}`}
              className="w-4 h-4"
            />
            <span className="ml-2 text-xs text-slate-500">
              {form[key] ? "Yes" : "No"}
            </span>
          </div>
        ) : (
          <Input
            type={type || "text"}
            className="h-9 text-xs"
            value={form[key] || ""}
            onChange={(e) => set(key, e.target.value)}
            data-testid={`f-${key}`}
          />
        )}
      </div>
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      if (!form.first_name) {
        toast.error("First name is required");
        setBusy(false);
        return;
      }
      if (!String(form.gender || "").trim()) {
        toast.error("Gender is required");
        setBusy(false);
        return;
      }
      // Always attach branch_id and session_id from context (unless superadmin set their own)
      const payload = {
        ...form,
        gender: String(form.gender || "").trim(),
        branch_id: form.branch_id || branchId,
        session_id: form.session_id || sessionId,
      };
      if ((payload.has_sibling || "no") !== "yes") {
        payload.sibling_student_id = "";
      }
      if (isEdit) {
        await api.put(`/students/${id}`, payload);
        toast.success("Student updated");
      } else {
        await api.post("/students", payload);
        toast.success("Admission created — fees auto-assigned for class");
        nav("/students");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === "string" ? msg : "Save failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="admission-page">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => nav("/students")}
            className="h-8 flex-shrink-0"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-bold tracking-tight font-serif-display erp-text-primary truncate">
              {isEdit ? "Edit Student" : "New Admission"}
            </h1>
            <div className="text-[11px] text-slate-500">
              Branch:{" "}
              <span className="font-medium">
                {branches.find((b) => b.id === branchId)?.name || "—"}
              </span>{" "}
              · auto-attached
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="h-8 erp-primary flex-shrink-0"
          data-testid="save-admission"
        >
          <Save className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto bg-slate-100 p-0.5 gap-0.5">
          {TABS.map((s) => (
            <TabsTrigger
              key={s.key}
              value={s.key}
              className="text-xs px-2.5 py-1.5 data-[state=active]:bg-white"
              data-testid={`tab-${s.key}`}
            >
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((s) => (
          <TabsContent
            key={s.key}
            value={s.key}
            className="border border-slate-200 bg-white rounded-md p-3 md:p-5 mt-2 shadow-sm"
          >
            {s.key === "docs" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {FIELDS.docs.map(([key, label]) => (
                  <FileUploader
                    key={key}
                    label={label}
                    value={form[key]}
                    onChange={(v) => set(key, v)}
                    tag={key}
                    enforceMaxSize={false}
                    enableCrop={key === "doc_student_photo"}
                    testid={`doc-${key}`}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {FIELDS[s.key].map(renderField)}
              </div>
            )}
            {/* SuperAdmin branch override */}
            {s.key === "student" && isSuperAdmin && (
              <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
                <Label className="text-[11px] font-semibold text-amber-700 mb-1 block">
                  SuperAdmin: Branch override
                </Label>
                <select
                  className="h-9 w-72 rounded-md border border-amber-300 text-xs px-2 bg-amber-50"
                  value={form.branch_id || ""}
                  onChange={(e) => set("branch_id", e.target.value)}
                  data-testid="branch-override"
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
